import React, { useState, useMemo, Fragment } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Landmark, Plus, Save, X, Search, ChevronDown, ChevronRight, DollarSign, Shield,
  Upload, Sparkles, FileText, Calendar, CheckCircle2, AlertCircle, Loader2,
  Pencil, Trash2, PlusCircle,
} from 'lucide-react';

/* ---------- types ---------- */
interface Entity { id: string; name: string; group_tag: string; }

interface BankAccount {
  id: string; entity_id: string; entity_name: string;
  bank_name: string; iban: string | null; account_number: string | null;
  currency: string; account_type: string | null; is_active: boolean;
  notes: string | null;
}

interface WeeklyBalance {
  id: string;
  entity_id: string;
  entity_name: string;
  bank_account_id: string | null;
  bank_name: string | null;
  week_start: string;
  balance: number;
  currency: string;
  source: string;
  notes: string | null;
}

interface ManualBalance {
  id: string;
  bank_account_id: string | null;
  entity_id: string;
  entity_name?: string;
  bank_name?: string;
  balance_date: string;          // YYYY-MM-DD
  balance: number;
  currency: string;
  notes: string | null;
  entered_by: string | null;
  entered_at: string;
}

interface ParsedStatement {
  bank_name: string | null;
  account_identifier: string | null;
  currency: string | null;
  period_from: string | null;
  period_to: string | null;
  opening_balance: number | null;
  closing_balance: number | null;
}

/* ---------- helpers ---------- */
function fmtRaw(n: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

// fmtCurrency now provided via useCurrency — call useBankFmt inside components
function useBankFmt() {
  const { fmt } = useCurrency();
  return {
    fmt: (n: number) => fmtRaw(n),
    fmtCurrency: (amount: number, currency: string) => fmt(amount, currency),
  };
}

function getMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return d.toISOString().split('T')[0];
}

function getFirstOfMonth(date?: Date): string {
  const d = date || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtMonth(dateStr: string): string {
  // dateStr is YYYY-MM-DD; render as "Apr 2026"
  const [y, m] = dateStr.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/* ---------- main component ---------- */
interface CryptoAccount {
  id: string; entity_id: string; entity_name: string;
  platform: string; account_label: string | null; account_type: string;
  custody_type: string | null; is_active: boolean; api_key_env_var: string | null;
  notes: string | null;
}

export default function BankAccounts() {
  const { fmtCurrency } = useBankFmt();
  const { data: accounts, loading: loadingAccounts, refresh: refreshAccounts } = useFetch<BankAccount[]>('/api/fin/bank-accounts');
  const { data: cryptoAccounts, loading: loadingCrypto } = useFetch<CryptoAccount[]>('/api/fin/crypto-accounts');
  const { data: balancesData, loading: loadingBalances, refresh: refreshBalances } = useFetch<{ balances: WeeklyBalance[] }>('/api/fin/weekly-balances');
  const { data: manualBalancesData, refresh: refreshManualBalances } = useFetch<{ balances: ManualBalance[] }>('/api/fin/manual-balances');
  const { data: entities } = useFetch<Entity[]>('/api/fin/entities');

  const [showRecordBalance, setShowRecordBalance] = useState(false);
  const [showOpeningBalance, setShowOpeningBalance] = useState(false);

  const manualBalances = manualBalancesData?.balances || [];
  const balances = balancesData?.balances || [];

  const loading = loadingAccounts || loadingBalances;

  const cryptoGrouped = useMemo(() => {
    const groups = new Map<string, { entityName: string; accounts: CryptoAccount[] }>();
    for (const a of (cryptoAccounts || []).filter(a => a.is_active)) {
      if (!groups.has(a.entity_id)) groups.set(a.entity_id, { entityName: a.entity_name, accounts: [] });
      groups.get(a.entity_id)!.accounts.push(a);
    }
    return Array.from(groups.values()).sort((a, b) => a.entityName.localeCompare(b.entityName));
  }, [cryptoAccounts]);

  if (loading && !accounts) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48 mb-2" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Landmark size={24} /> Accounts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {accounts?.length || 0} bank accounts, {cryptoAccounts?.length || 0} crypto accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowRecordBalance(true)}>
            <DollarSign size={14} /> Weekly Balance
          </Button>
          <Button size="sm" className="gap-2" onClick={() => setShowOpeningBalance(true)}>
            <Calendar size={14} /> Opening Balance
          </Button>
        </div>
      </div>

      <Tabs defaultValue="bank" className="w-full">
        <TabsList>
          <TabsTrigger value="bank">Bank Accounts ({accounts?.length || 0})</TabsTrigger>
          <TabsTrigger value="opening">Opening Balances</TabsTrigger>
          <TabsTrigger value="crypto">Crypto Accounts ({cryptoAccounts?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="bank" className="space-y-4 mt-4">
          <BankAccountTableView
            accounts={accounts || []}
            entities={entities || []}
            weeklyBalances={balances}
            manualBalances={manualBalances}
            onAccountsChanged={refreshAccounts}
            onBalancesChanged={() => { refreshBalances(); refreshManualBalances(); }}
          />
        </TabsContent>

        <TabsContent value="opening" className="space-y-6 mt-4">
          <OpeningBalancesTab
            accounts={accounts || []}
            entities={entities || []}
            manualBalances={manualBalances}
            onOpenEntry={() => setShowOpeningBalance(true)}
          />
        </TabsContent>

        <TabsContent value="crypto" className="space-y-4 mt-4">
          {loadingCrypto ? (
            <Skeleton className="h-40 w-full" />
          ) : cryptoGrouped.length === 0 ? (
            <Card className="shadow-xs"><CardContent className="p-6 text-center text-muted-foreground">No crypto accounts found.</CardContent></Card>
          ) : (
            cryptoGrouped.map(group => (
              <Card key={group.entityName} className="shadow-xs">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Shield size={14} className="text-primary" />
                    {group.entityName}
                    <Badge variant="outline" className="text-xs">{group.accounts.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Platform</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Custody</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.accounts.map(a => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{a.platform}</TableCell>
                          <TableCell className="text-muted-foreground">{a.account_label || '—'}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{a.account_type}</Badge></TableCell>
                          <TableCell className="text-muted-foreground">{a.custody_type || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Record Balance Dialog */}
      {showRecordBalance && (
        <RecordBalanceDialog
          entities={entities || []}
          accounts={accounts || []}
          onClose={() => setShowRecordBalance(false)}
          onSaved={() => { setShowRecordBalance(false); refreshBalances(); }}
        />
      )}

      {/* Opening Balance Dialog (manual + statement upload) */}
      {showOpeningBalance && (
        <OpeningBalanceDialog
          entities={entities || []}
          accounts={accounts || []}
          onClose={() => setShowOpeningBalance(false)}
          onSaved={() => { setShowOpeningBalance(false); refreshManualBalances(); }}
        />
      )}
    </div>
  );
}

/* ---------- Record Balance Dialog ---------- */
function RecordBalanceDialog({ entities, accounts, onClose, onSaved }: {
  entities: Entity[];
  accounts: BankAccount[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [entityId, setEntityId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [weekStart, setWeekStart] = useState(getMonday());
  const [balance, setBalance] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredAccounts = accounts.filter(a => a.entity_id === entityId && a.is_active);

  const handleSave = async () => {
    if (!entityId || !balance) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch('/api/fin/weekly-balances', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          entity_id: entityId,
          bank_account_id: bankAccountId || null,
          week_start: weekStart,
          balance: parseFloat(balance),
          currency,
          source: 'manual',
          notes: notes || null,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Weekly Balance</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Entity</Label>
            <Select value={entityId || '__none__'} onValueChange={v => { setEntityId(v === '__none__' ? '' : v); setBankAccountId(''); }}>
              <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select...</SelectItem>
                {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Bank Account (optional)</Label>
            <Select value={bankAccountId || '__none__'} onValueChange={v => setBankAccountId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Entity-level (no specific account)</SelectItem>
                {filteredAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.bank_name} ({a.currency})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Week Start (Monday)</Label>
            <Input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Balance</Label>
              <Input type="number" step="0.01" value={balance} onChange={e => setBalance(e.target.value)} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !entityId || !balance}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Opening Balances Tab ---------- */
function OpeningBalancesTab({
  accounts,
  entities,
  manualBalances,
  onOpenEntry,
}: {
  accounts: BankAccount[];
  entities: Entity[];
  manualBalances: ManualBalance[];
  onOpenEntry: () => void;
}) {
  const { fmtCurrency } = useBankFmt();
  const [selectedMonth, setSelectedMonth] = useState<string>(getFirstOfMonth());

  // Build list of available months from manual balances
  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    months.add(getFirstOfMonth());
    for (const b of manualBalances) {
      // Treat balances dated on the 1st as monthly opening balances
      if (b.balance_date.endsWith('-01')) months.add(b.balance_date);
    }
    return Array.from(months).sort().reverse();
  }, [manualBalances]);

  // Lookup: bank_account_id → opening balance for selected month
  const balanceByAccount = useMemo(() => {
    const map = new Map<string, ManualBalance>();
    for (const b of manualBalances) {
      if (b.balance_date === selectedMonth && b.bank_account_id) {
        map.set(b.bank_account_id, b);
      }
    }
    return map;
  }, [manualBalances, selectedMonth]);

  // Group accounts by entity
  const grouped = useMemo(() => {
    const groups = new Map<string, { entityName: string; entityId: string; accounts: BankAccount[] }>();
    for (const a of accounts.filter(x => x.is_active)) {
      if (!groups.has(a.entity_id)) {
        groups.set(a.entity_id, { entityName: a.entity_name, entityId: a.entity_id, accounts: [] });
      }
      groups.get(a.entity_id)!.accounts.push(a);
    }
    return Array.from(groups.values()).sort((a, b) => a.entityName.localeCompare(b.entityName));
  }, [accounts]);

  // Coverage stats
  const totalAccounts = accounts.filter(a => a.is_active).length;
  const filledAccounts = balanceByAccount.size;
  const coveragePct = totalAccounts > 0 ? Math.round((filledAccounts / totalAccounts) * 100) : 0;

  // Currency totals for the selected month
  const currencyTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const b of balanceByAccount.values()) {
      totals.set(b.currency, (totals.get(b.currency) || 0) + Number(b.balance));
    }
    return Array.from(totals.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [balanceByAccount]);

  return (
    <>
      {/* Treasury workflow guide */}
      <Card className="shadow-xs border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Calendar size={16} className="text-primary" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-medium text-foreground mb-1">Treasury Workflow — Monthly Opening Balances</div>
              <div className="text-muted-foreground text-xs space-y-1">
                <div>1. Pull the bank statement for the first day of the month from each bank portal.</div>
                <div>2. Click <strong>Enter Opening Balance</strong>, pick the entity & account.</div>
                <div>3. Either type the balance manually <em>or</em> upload the statement and let AI extract opening/closing balances automatically.</div>
                <div>4. Review and save. The balance becomes the starting point for that month's cash flow.</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Toolbar: month picker + entry button + coverage */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Label className="text-sm whitespace-nowrap">Period:</Label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableMonths.map(m => (
                <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant={coveragePct === 100 ? 'default' : 'secondary'} className="text-xs">
            {filledAccounts}/{totalAccounts} accounts ({coveragePct}%)
          </Badge>
        </div>
        <Button size="sm" className="gap-2" onClick={onOpenEntry}>
          <Plus size={14} /> Enter Opening Balance
        </Button>
      </div>

      {/* Currency totals */}
      {currencyTotals.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {currencyTotals.map(([ccy, total]) => (
            <Card key={ccy} className="shadow-xs">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Total {ccy}
                </div>
                <div className="text-xl font-bold font-mono">{fmtCurrency(total, ccy)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Per-entity table */}
      <div className="space-y-4">
        {grouped.map(group => {
          const groupFilled = group.accounts.filter(a => balanceByAccount.has(a.id)).length;
          return (
            <Card key={group.entityId} className="shadow-xs">
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <div className="font-semibold text-sm">{group.entityName}</div>
                  <div className="text-xs text-muted-foreground">
                    {groupFilled}/{group.accounts.length} accounts entered
                  </div>
                </div>
                {groupFilled === group.accounts.length ? (
                  <CheckCircle2 size={18} className="text-green-600" />
                ) : (
                  <AlertCircle size={18} className="text-amber-500" />
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Bank</TableHead>
                    <TableHead className="text-xs">IBAN</TableHead>
                    <TableHead className="text-xs">CCY</TableHead>
                    <TableHead className="text-xs text-right">Opening Balance</TableHead>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.accounts.map(a => {
                    const bal = balanceByAccount.get(a.id);
                    return (
                      <TableRow key={a.id}>
                        <TableCell className="text-sm font-medium">{a.bank_name}</TableCell>
                        <TableCell className="text-sm font-mono text-xs text-muted-foreground">
                          {a.iban || a.account_number || '-'}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{a.currency}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {bal ? fmtCurrency(Number(bal.balance), bal.currency) : (
                            <span className="text-muted-foreground italic">not entered</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {bal?.entered_by || '-'}
                        </TableCell>
                        <TableCell>
                          {bal ? (
                            <Badge variant="default" className="text-[10px] gap-1">
                              <CheckCircle2 size={10} /> Entered
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Pending</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          );
        })}
        {grouped.length === 0 && (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No bank accounts found.</CardContent></Card>
        )}
      </div>
      {/* entities is unused in this tab but passed for future filtering */}
      {entities.length === 0 && null}
    </>
  );
}

/* ---------- Opening Balance Dialog (manual + statement upload) ---------- */
function OpeningBalanceDialog({
  entities,
  accounts,
  onClose,
  onSaved,
}: {
  entities: Entity[];
  accounts: BankAccount[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { fmtCurrency } = useBankFmt();
  const [mode, setMode] = useState<'manual' | 'upload'>('manual');

  // Form state
  const [entityId, setEntityId] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [balanceDate, setBalanceDate] = useState(getFirstOfMonth());
  const [balance, setBalance] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [notes, setNotes] = useState('');

  // Upload + parse state
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedStatement | null>(null);
  const [statementFile, setStatementFile] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const filteredAccounts = accounts.filter(a => a.entity_id === entityId && a.is_active);

  // When account selected, default currency to account currency
  const handleAccountChange = (id: string) => {
    setBankAccountId(id);
    const acct = accounts.find(a => a.id === id);
    if (acct) setCurrency(acct.currency);
  };

  const handleFileUpload = async (file: File) => {
    setParsing(true);
    setParseError(null);
    setParsed(null);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip data URL prefix
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch('/api/fin/parse-statement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: base64, file_type: file.type }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.parsed) {
        throw new Error('AI could not extract structured data from this statement.');
      }

      setParsed(data.parsed);
      setStatementFile(file.name);

      // Pre-fill form from parsed data
      if (data.parsed.opening_balance != null) setBalance(String(data.parsed.opening_balance));
      if (data.parsed.currency) setCurrency(data.parsed.currency);
      if (data.parsed.period_from) {
        // Snap period_from to first of month for opening balance
        const d = new Date(data.parsed.period_from);
        setBalanceDate(getFirstOfMonth(d));
      }

      // Try to auto-match the bank account by IBAN/identifier
      if (data.parsed.account_identifier) {
        const ident = String(data.parsed.account_identifier).replace(/\s+/g, '').toUpperCase();
        const matched = accounts.find(a =>
          (a.iban && a.iban.replace(/\s+/g, '').toUpperCase() === ident) ||
          (a.account_number && a.account_number.replace(/\s+/g, '').toUpperCase() === ident)
        );
        if (matched) {
          setEntityId(matched.entity_id);
          setBankAccountId(matched.id);
          setCurrency(matched.currency);
        }
      }
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse statement');
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    if (!entityId || !balance || !balanceDate) return;
    setSaving(true);
    setSaveError(null);
    try {
      const fullNotes = [
        notes,
        statementFile ? `Source: ${statementFile}` : null,
        parsed?.closing_balance != null ? `Closing balance: ${parsed.closing_balance}` : null,
      ].filter(Boolean).join(' | ');

      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch('/api/fin/manual-balances', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          entity_id: entityId,
          bank_account_id: bankAccountId || null,
          balance_date: balanceDate,
          balance: parseFloat(balance),
          currency,
          notes: fullNotes || null,
          // entered_by is set server-side from the JWT; client value kept only
          // as a source hint for unauthenticated callers (seed scripts).
          entered_by: mode === 'upload' ? 'treasury (ai-parsed)' : 'treasury (manual)',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar size={18} /> Enter Opening Balance
          </DialogTitle>
        </DialogHeader>

        <Tabs value={mode} onValueChange={v => setMode(v as 'manual' | 'upload')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual" className="gap-2"><FileText size={14} /> Manual Entry</TabsTrigger>
            <TabsTrigger value="upload" className="gap-2"><Sparkles size={14} /> Upload Statement (AI)</TabsTrigger>
          </TabsList>

          {/* Upload mode */}
          <TabsContent value="upload" className="space-y-4 mt-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              {parsing ? (
                <div className="space-y-2">
                  <Loader2 size={24} className="mx-auto animate-spin text-primary" />
                  <div className="text-sm text-muted-foreground">Parsing statement with AI...</div>
                </div>
              ) : parsed ? (
                <div className="space-y-2 text-left">
                  <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                    <CheckCircle2 size={16} /> Parsed successfully
                  </div>
                  <div className="text-xs space-y-1 text-muted-foreground">
                    <div><strong>Bank:</strong> {parsed.bank_name || '—'}</div>
                    <div><strong>Account:</strong> {parsed.account_identifier || '—'}</div>
                    <div><strong>Period:</strong> {parsed.period_from || '?'} → {parsed.period_to || '?'}</div>
                    <div><strong>Opening:</strong> {parsed.opening_balance != null ? fmtCurrency(parsed.opening_balance, parsed.currency || currency) : '—'}</div>
                    <div><strong>Closing:</strong> {parsed.closing_balance != null ? fmtCurrency(parsed.closing_balance, parsed.currency || currency) : '—'}</div>
                  </div>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => { setParsed(null); setStatementFile(null); }}>
                    Upload another file
                  </Button>
                </div>
              ) : (
                <>
                  <Upload size={28} className="mx-auto text-muted-foreground mb-2" />
                  <div className="text-sm font-medium mb-1">Upload bank statement</div>
                  <div className="text-xs text-muted-foreground mb-3">PDF, PNG, or JPG. AI will extract opening &amp; closing balances.</div>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    id="statement-upload"
                    className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
                  />
                  <label htmlFor="statement-upload">
                    <Button size="sm" variant="outline" asChild>
                      <span className="cursor-pointer"><Upload size={14} className="mr-1" /> Choose file</span>
                    </Button>
                  </label>
                </>
              )}
              {parseError && (
                <div className="mt-3 text-xs text-destructive flex items-center gap-1 justify-center">
                  <AlertCircle size={12} /> {parseError}
                </div>
              )}
            </div>
            {parsed && (
              <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
                Review the auto-filled fields below, adjust if needed, then save.
              </div>
            )}
          </TabsContent>

          {/* Manual mode */}
          <TabsContent value="manual" className="mt-4">
            <div className="text-xs text-muted-foreground mb-3">
              Enter the opening balance directly from your bank portal or statement.
            </div>
          </TabsContent>
        </Tabs>

        {/* Shared form fields */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Entity *</Label>
              <Select value={entityId || '__none__'} onValueChange={v => { setEntityId(v === '__none__' ? '' : v); setBankAccountId(''); }}>
                <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Bank Account *</Label>
              <Select value={bankAccountId || '__none__'} onValueChange={v => handleAccountChange(v === '__none__' ? '' : v)} disabled={!entityId}>
                <SelectTrigger><SelectValue placeholder={entityId ? 'Select account' : 'Pick entity first'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select...</SelectItem>
                  {filteredAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.bank_name} — {a.currency} {a.iban ? `(${a.iban.slice(-4)})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Month (1st) *</Label>
              <Input type="date" value={balanceDate} onChange={e => setBalanceDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Balance *</Label>
              <Input type="number" step="0.01" value={balance} onChange={e => setBalance(e.target.value)} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>Currency *</Label>
              <Input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional context (e.g. statement reference)" rows={2} />
          </div>

          {saveError && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle size={12} /> {saveError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !entityId || !bankAccountId || !balance || !balanceDate}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Opening Balance'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   TABLE VIEW — flat hierarchical registry with per-row CRUD + balance append
   ═══════════════════════════════════════════════════════════════════════ */

/** Pick the most recent balance per account from combined weekly + manual sources. */
function buildLatestByAccount(
  weekly: WeeklyBalance[],
  manual: ManualBalance[],
): Record<string, { value: number; currency: string; date: string; source: 'weekly' | 'manual' }> {
  const out: Record<string, { value: number; currency: string; date: string; source: 'weekly' | 'manual' }> = {};
  for (const w of weekly) {
    if (!w.bank_account_id) continue;
    const cur = out[w.bank_account_id];
    if (!cur || w.week_start > cur.date) {
      out[w.bank_account_id] = { value: Number(w.balance), currency: w.currency, date: w.week_start, source: 'weekly' };
    }
  }
  for (const m of manual) {
    if (!m.bank_account_id) continue;
    const cur = out[m.bank_account_id];
    if (!cur || m.balance_date > cur.date) {
      out[m.bank_account_id] = { value: Number(m.balance), currency: m.currency, date: m.balance_date, source: 'manual' };
    }
  }
  return out;
}

interface BankAccountTableViewProps {
  accounts: BankAccount[];
  entities: Entity[];
  weeklyBalances: WeeklyBalance[];
  manualBalances: ManualBalance[];
  onAccountsChanged: () => void;
  onBalancesChanged: () => void;
}

function BankAccountTableView({
  accounts, entities, weeklyBalances, manualBalances, onAccountsChanged, onBalancesChanged,
}: BankAccountTableViewProps) {
  const { fmtCurrency } = useBankFmt();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Default: all entities that have at least one account expanded
    const s = new Set<string>();
    for (const a of accounts) s.add(a.entity_id);
    return s;
  });
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [creatingFor, setCreatingFor] = useState<string | null>(null); // entity_id
  const [appending, setAppending] = useState<BankAccount | null>(null);

  const latestByAccount = useMemo(
    () => buildLatestByAccount(weeklyBalances, manualBalances),
    [weeklyBalances, manualBalances],
  );

  // Summary totals by currency — sum of latest balance per active account.
  // Uses the same latestByAccount map computed above so the numbers align
  // exactly with what each row shows in its Latest Balance column.
  const currencyTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const a of accounts) {
      if (!a.is_active) continue;
      const latest = latestByAccount[a.id];
      if (!latest) continue;
      totals.set(latest.currency, (totals.get(latest.currency) || 0) + latest.value);
    }
    return [...totals.entries()].sort((x, y) => x[0].localeCompare(y[0]));
  }, [accounts, latestByAccount]);

  // Filter + group by entity. Inactive accounts hidden unless toggled on.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = accounts.filter(a => {
      if (!showInactive && !a.is_active) return false;
      if (!q) return true;
      return (
        a.bank_name.toLowerCase().includes(q) ||
        a.entity_name.toLowerCase().includes(q) ||
        (a.iban || '').toLowerCase().includes(q) ||
        (a.account_number || '').toLowerCase().includes(q) ||
        a.currency.toLowerCase().includes(q)
      );
    });
    // Every entity in `entities` appears even if it has 0 accounts, so
    // treasury can add the first account for an entity from this view.
    const byId = new Map<string, BankAccount[]>();
    for (const e of entities) byId.set(e.id, []);
    for (const a of filtered) {
      if (!byId.has(a.entity_id)) byId.set(a.entity_id, []);
      byId.get(a.entity_id)!.push(a);
    }
    // Only include entities with accounts (after filter) OR all if search empty
    const out: { entityId: string; entityName: string; accounts: BankAccount[] }[] = [];
    for (const e of entities) {
      const acc = byId.get(e.id) || [];
      if (q && acc.length === 0) continue;
      out.push({ entityId: e.id, entityName: e.name, accounts: acc });
    }
    return out.sort((a, b) => a.entityName.localeCompare(b.entityName));
  }, [accounts, entities, search, showInactive]);

  const toggleEntity = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const expandAll = () => setExpanded(new Set(entities.map(e => e.id)));
  const collapseAll = () => setExpanded(new Set());

  const handleDelete = async (account: BankAccount) => {
    if (!window.confirm(`Deactivate "${account.bank_name}" (${account.iban || account.account_number || 'no IBAN'})?\n\nThe row stays in the database (other tables reference it) but will be hidden from active views.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch('/api/fin/bank-accounts', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ id: account.id }),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      onAccountsChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <>
      {/* Summary by currency — sum of latest balance per active account */}
      {currencyTotals.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {currencyTotals.map(([ccy, total]) => (
            <Card key={ccy} className="shadow-xs">
              <CardContent className="p-4">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Total {ccy}</div>
                <div className="text-xl font-bold font-mono">{fmtCurrency(total, ccy)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search entity, bank, IBAN, currency..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={expandAll}>Expand all</Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>Collapse all</Button>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="table-show-inactive" className="text-xs text-muted-foreground">Show inactive</Label>
          <Switch id="table-show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
        </div>
      </div>

      {/* The table */}
      <Card className="shadow-xs">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[32%]">Entity / Bank</TableHead>
              <TableHead>IBAN / Account</TableHead>
              <TableHead className="w-16">CCY</TableHead>
              <TableHead className="w-24">Type</TableHead>
              <TableHead className="w-20">Status</TableHead>
              <TableHead className="text-right w-40">Latest Balance</TableHead>
              <TableHead className="w-[124px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map(group => {
              const isExpanded = expanded.has(group.entityId);
              const activeCount = group.accounts.filter(a => a.is_active).length;
              return (
                <Fragment key={group.entityId}>
                  {/* Entity parent row */}
                  <TableRow
                    className="bg-muted/30 hover:bg-muted/50 cursor-pointer font-semibold"
                    onClick={() => toggleEntity(group.entityId)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        {isExpanded
                          ? <ChevronDown size={14} className="shrink-0" />
                          : <ChevronRight size={14} className="shrink-0" />}
                        <span className="text-sm">{group.entityName}</span>
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {activeCount} active / {group.accounts.length} total
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell colSpan={5} />
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={e => { e.stopPropagation(); setCreatingFor(group.entityId); }}
                      >
                        <PlusCircle size={12} className="mr-1" /> Add
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Child account rows */}
                  {isExpanded && group.accounts.map(a => {
                    const latest = latestByAccount[a.id];
                    return (
                      <TableRow key={a.id} className={!a.is_active ? 'opacity-50' : ''}>
                        <TableCell className="pl-10 py-2 text-sm">{a.bank_name}</TableCell>
                        <TableCell className="font-mono text-xs py-2">
                          {a.iban || a.account_number || '—'}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge variant="secondary" className="text-[10px]">{a.currency}</Badge>
                        </TableCell>
                        <TableCell className="py-2 text-xs text-muted-foreground">
                          {a.account_type || '—'}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge variant={a.is_active ? 'default' : 'secondary'} className="text-[10px]">
                            {a.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs py-2">
                          {latest ? (
                            <>
                              <div>{fmtCurrency(latest.value, latest.currency)}</div>
                              <div className="text-[9px] text-muted-foreground font-sans">
                                {new Date(latest.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </div>
                            </>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right py-2">
                          <div className="inline-flex items-center gap-0.5">
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6"
                              title="Append balance"
                              onClick={() => setAppending(a)}
                            >
                              <DollarSign size={12} />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6"
                              title="Edit account"
                              onClick={() => setEditing(a)}
                            >
                              <Pencil size={12} />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive"
                              title="Deactivate account"
                              onClick={() => handleDelete(a)}
                            >
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {isExpanded && group.accounts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="pl-10 py-2 text-xs text-muted-foreground italic">
                        No accounts yet — click "Add" to create one.
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}

            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  No entities match the current filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create / Edit dialog */}
      {(editing || creatingFor) && (
        <BankAccountFormDialog
          account={editing}
          defaultEntityId={creatingFor || undefined}
          entities={entities}
          onClose={() => { setEditing(null); setCreatingFor(null); }}
          onSaved={() => { setEditing(null); setCreatingFor(null); onAccountsChanged(); }}
        />
      )}

      {/* Append balance dialog */}
      {appending && (
        <AppendBalanceDialog
          account={appending}
          onClose={() => setAppending(null)}
          onSaved={() => { setAppending(null); onBalancesChanged(); }}
        />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   BANK ACCOUNT CREATE/EDIT DIALOG
   ═══════════════════════════════════════════════════════════════════════ */

interface BankAccountFormDialogProps {
  account: BankAccount | null;     // edit mode when set
  defaultEntityId?: string;        // create mode pre-selected entity
  entities: Entity[];
  onClose: () => void;
  onSaved: () => void;
}

function BankAccountFormDialog({ account, defaultEntityId, entities, onClose, onSaved }: BankAccountFormDialogProps) {
  const isEdit = !!account;
  const [entityId, setEntityId] = useState(account?.entity_id || defaultEntityId || '');
  const [bankName, setBankName] = useState(account?.bank_name || '');
  const [iban, setIban] = useState(account?.iban || '');
  const [accountNumber, setAccountNumber] = useState(account?.account_number || '');
  const [currency, setCurrency] = useState(account?.currency || 'EUR');
  const [accountType, setAccountType] = useState(account?.account_type || 'Own');
  const [notes, setNotes] = useState(account?.notes || '');
  const [isActive, setIsActive] = useState(account?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = !!entityId && !!bankName && !!currency && currency.length <= 3;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const payload: Record<string, any> = {
        entity_id: entityId,
        bank_name: bankName.trim(),
        iban: iban.trim() || null,
        account_number: accountNumber.trim() || null,
        currency: currency.trim().toUpperCase(),
        account_type: accountType.trim() || null,
        notes: notes.trim() || null,
      };
      let res: Response;
      if (isEdit) {
        payload.id = account!.id;
        payload.is_active = isActive;
        res = await fetch('/api/fin/bank-accounts', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/fin/bank-accounts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark size={18} /> {isEdit ? 'Edit Bank Account' : 'Add Bank Account'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Entity</Label>
            <Select value={entityId || '__none__'} onValueChange={v => setEntityId(v === '__none__' ? '' : v)} disabled={isEdit}>
              <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select...</SelectItem>
                {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Bank / Institution</Label>
            <Input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="e.g. Bank of Cyprus" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>IBAN</Label>
              <Input value={iban} onChange={e => setIban(e.target.value)} placeholder="Optional" className="font-mono text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label>Account number</Label>
              <Input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="Optional" className="font-mono text-xs" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} maxLength={3} placeholder="EUR" />
            </div>
            <div className="space-y-1.5">
              <Label>Account type</Label>
              <Select value={accountType || '__none__'} onValueChange={v => setAccountType(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Account type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="Own">Own</SelectItem>
                  <SelectItem value="Client">Client</SelectItem>
                  <SelectItem value="Own/Client">Own/Client</SelectItem>
                  <SelectItem value="Safeguard">Safeguard</SelectItem>
                  <SelectItem value="Payment">Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional" />
          </div>

          {isEdit && (
            <div className="flex items-center justify-between border-t pt-3">
              <Label htmlFor="edit-is-active" className="text-sm">Active</Label>
              <Switch id="edit-is-active" checked={isActive} onCheckedChange={setIsActive} />
            </div>
          )}

          {error && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   APPEND BALANCE DIALOG — writes to fin.manual_balances
   ═══════════════════════════════════════════════════════════════════════ */

interface AppendBalanceDialogProps {
  account: BankAccount;
  onClose: () => void;
  onSaved: () => void;
}

function AppendBalanceDialog({ account, onClose, onSaved }: AppendBalanceDialogProps) {
  const [balance, setBalance] = useState('');
  const [balanceDate, setBalanceDate] = useState(getFirstOfMonth());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!balance || !balanceDate) return;
    const num = parseFloat(balance);
    if (isNaN(num)) { setError('Balance must be a number'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const res = await fetch('/api/fin/manual-balances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          entity_id: account.entity_id,
          bank_account_id: account.id,
          balance_date: balanceDate,
          balance: num,
          currency: account.currency,
          notes: notes.trim() || null,
          // server overrides entered_by from JWT; fallback kept for unauthenticated callers
          entered_by: 'treasury (table view)',
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign size={18} /> Append Balance
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Read-only account context */}
          <div className="rounded-md border bg-muted/20 p-3 space-y-1 text-xs">
            <div><span className="text-muted-foreground">Entity:</span> <span className="font-medium">{account.entity_name}</span></div>
            <div><span className="text-muted-foreground">Bank:</span> <span className="font-medium">{account.bank_name}</span></div>
            {account.iban && <div className="font-mono"><span className="text-muted-foreground font-sans">IBAN:</span> {account.iban}</div>}
            <div><span className="text-muted-foreground">Currency:</span> <Badge variant="secondary" className="text-[10px]">{account.currency}</Badge></div>
          </div>

          <div className="space-y-1.5">
            <Label>Balance date</Label>
            <Input type="date" value={balanceDate} onChange={e => setBalanceDate(e.target.value)} />
            <p className="text-[10px] text-muted-foreground">
              Dates on the 1st of the month are treated as monthly opening balances.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Balance amount ({account.currency})</Label>
            <Input
              type="number" step="0.01" value={balance}
              onChange={e => setBalance(e.target.value)}
              placeholder="0.00" className="font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional (e.g. source statement)" />
          </div>

          {error && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={!balance || !balanceDate || saving}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Balance'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
